<script lang="ts">
  import { Cancellation, currentNonRefundableFees, notToBeRefunded } from "@models/cancellations/Cancellation"
  import {
    insuranceIsNonRefundable,
    isForInsurance,
    currentNonRefundableInsurance,
    NonRefundableAdjustment,
    totalNonRefundable,
  } from "@models/cancellations/NonRefundableAdjustment"
  import { InsurancePlan } from "@models/insurance/InsurancePlan"
  import { Person } from "@models/profiles/Person"
  import { formatCentsAsUsd } from "@util/money"
  import AmountInput from "@components/ui/AmountInput.svelte"
  import BooleanCheckbox from "@components/ui/BooleanCheckbox.svelte"
  import { ValidationErrors } from "@util/validations"
  import ErrorMessage from "@components/ui/ErrorMessage.svelte"

  export let person: Person
  export let cancellation: Cancellation
  export let insurancePlan: InsurancePlan | undefined
  export let errors: ValidationErrors<Cancellation> = {}
  export let submitted: boolean = false
  export let insuranceOnly: boolean = false

  function insuranceDescription(
    adjustment: NonRefundableAdjustment,
    insurancePlan: InsurancePlan,
    insuranceOnly: boolean
  ): string {
    if (insuranceOnly || insuranceIsNonRefundable(cancellation.nonRefundableAdjustments)) {
      return `${insurancePlan.name} (${formatCentsAsUsd(adjustment.price)}) is non-refundable`
    } else {
      return `${insurancePlan.name} (${formatCentsAsUsd(adjustment.price)}) will be cancelled and refunded`
    }
  }
</script>

<div class="grid gap-block-2" data-role="non-refundables">
  {#each cancellation.nonRefundableAdjustments as adjustment, idx}
    <div class="grid gap-2 grid-cols-2 sm:grid-cols-4 items-center" data-role="non-refundable-item">
      <div class="sm:col-span-3 grid items-center" data-role="description">
        <label
          for="non-refundable-adjustment-input-{idx}"
          aria-controls="total-non-refundables-status coverage-details-status amounts-overview"
        >
          {#if insurancePlan && isForInsurance(adjustment)}
            {insuranceDescription(adjustment, insurancePlan, insuranceOnly)}
          {:else}
            {adjustment.description}
          {/if}
        </label>
      </div>

      <div class="sm:text-right">
        {#if isForInsurance(adjustment)}
          <input
            id="non-refundable-adjustment-input-{idx}"
            type="text"
            name="amount"
            value={formatCentsAsUsd(currentNonRefundableInsurance(cancellation.nonRefundableAdjustments, insuranceOnly))}
            class="text-right p-1 pr-4"
            data-role="amount-input"
            disabled={true}
          />
        {:else}
          <AmountInput
            id="non-refundable-adjustment-input-{idx}"
            name="amount"
            bind:value={adjustment.price}
            class="text-right p-1"
            dataRole="amount-input"
          />
        {/if}
      </div>
    </div>
  {/each}

  {#if cancellation.totalConvenienceFeesFromPayments > 0}
    <div class="grid gap-2 sm:grid-cols-4" data-role="non-refundable-item">
      <div class="sm:col-span-3 -mt-1" data-role="description">
        <BooleanCheckbox
          name="refundConvenienceFees"
          label="Convenience Fees of {formatCentsAsUsd(cancellation.totalConvenienceFeesFromPayments)} are non-refundable"
          bind:value={cancellation.feesAreNonRefundable}
          dataRole="fees-are-non-refundable"
          disabled={totalNonRefundable(cancellation.nonRefundableAdjustments, insuranceOnly) > 0}
        />
      </div>
      <div class="sm:text-right">
        <input
          type="text"
          name="convenienceFees"
          class="text-right p-1 pr-4"
          value={formatCentsAsUsd(currentNonRefundableFees(cancellation))}
          data-role="amount-input"
          disabled={true}
        />
      </div>
    </div>
  {/if}

  {#if cancellation.totalAffirmFeesFromPayments > 0}
    <div class="grid gap-2 sm:grid-cols-4" data-role="non-refundable-item">
      <div class="sm:col-span-3 -mt-1" data-role="description">
        <BooleanCheckbox
          name="refundAffirmFees"
          label="Affirm Fees of {formatCentsAsUsd(cancellation.totalAffirmFeesFromPayments)} are non-refundable"
          bind:value={cancellation.feesAreNonRefundable}
          dataRole="fees-are-non-refundable"
          disabled={totalNonRefundable(cancellation.nonRefundableAdjustments, insuranceOnly) > 0}
        />
      </div>
      <div class="sm:text-right">
        <input
          type="text"
          name="affirmFees"
          class="text-right p-1 pr-4"
          value={formatCentsAsUsd(currentNonRefundableFees(cancellation))}
          data-role="amount-input"
          disabled={true}
        />
      </div>
    </div>
  {/if}
</div>

<ErrorMessage when={submitted} error={errors.nonRefundableAdjustments} />

<div class="grid grid-cols-4 sm:mt-2 mt-4 font-bold text-gray-900" role="status" id="total-non-refundables-status">
  <div class="col-span-3">
    Total Non-Refundable for {person.firstName}
  </div>

  <div class="text-right" data-role="total-paid">
    {formatCentsAsUsd(notToBeRefunded(cancellation, insuranceOnly))}
  </div>
</div>
